/**
 * 最小限のクライアントルータ。
 *
 * 画面は 5 つだけで、必要なのは pathname の読み取り・push・戻るだけなので
 * ライブラリを足さず History API を直接使う。
 * Phase 5 の通知は `/shopping-list` へ遷移する（SPEC §10.3）ため、
 * ハッシュではなくパスで表現する。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Route =
  | { name: 'home' }
  | { name: 'dashboard' }
  | { name: 'item'; itemId: string }
  | { name: 'shopping-list' }
  | { name: 'settings' };

export function parsePath(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/dashboard') return { name: 'dashboard' };
  if (path === '/shopping-list') return { name: 'shopping-list' };
  if (path === '/settings') return { name: 'settings' };
  const m = /^\/items\/([^/]+)$/.exec(path);
  if (m) return { name: 'item', itemId: decodeURIComponent(m[1]) };
  return { name: 'home' };
}

export function routePath(route: Route): string {
  switch (route.name) {
    case 'dashboard':
      return '/dashboard';
    case 'shopping-list':
      return '/shopping-list';
    case 'settings':
      return '/settings';
    case 'item':
      return `/items/${encodeURIComponent(route.itemId)}`;
    case 'home':
      return '/';
  }
}

interface RouterValue {
  route: Route;
  navigate: (to: Route, opts?: { replace?: boolean }) => void;
  back: () => void;
}

const RouterContext = createContext<RouterValue>({
  route: { name: 'home' },
  navigate: () => {},
  back: () => {},
});

export const useRouter = (): RouterValue => useContext(RouterContext);

export function RouterProvider({ children }: { children: ReactNode }): ReactNode {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: Route, opts: { replace?: boolean } = {}) => {
    const path = routePath(to);
    if (opts.replace) window.history.replaceState(null, '', path);
    else window.history.pushState(null, '', path);
    setRoute(to);
    window.scrollTo(0, 0);
  }, []);

  const back = useCallback(() => {
    // 直接 URL で開かれた場合は戻り先が無いのでホームへ寄せる
    if (window.history.length > 1) window.history.back();
    else navigate({ name: 'home' }, { replace: true });
  }, [navigate]);

  const value = useMemo(() => ({ route, navigate, back }), [route, navigate, back]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}
